import { useCallback, useMemo, useState, useSyncExternalStore } from 'react'

/**
 * Draws only the rows in or near the viewport of a list that scrolls with the
 * page, so a list of tens of thousands stays as light as one of twenty. Rows
 * have known heights, so positions are worked out up front: no measuring.
 */
export function useWindowVirtualizer(heights: readonly number[], overscanPx = 600) {
  // A callback ref kept in state, so the list re-measures when it mounts.
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const offsets = useMemo(() => {
    const result = new Array<number>(heights.length + 1)
    result[0] = 0
    for (let index = 0; index < heights.length; index += 1) result[index + 1] = (result[index] ?? 0) + (heights[index] ?? 0)
    return result
  }, [heights])
  const total = offsets[heights.length] ?? 0
  // Where the screen is over the list, read from the page as it scrolls. It's
  // rounded to 50px, so scrolling re-renders every 50px rather than every pixel;
  // the overscan covers the difference.
  const position = useSyncExternalStore(
    subscribeToViewport,
    () => `${container ? Math.floor(-container.getBoundingClientRect().top / 50) * 50 : 0}:${window.innerHeight}`,
    () => '0:1000',
  )
  const [top = 0, height = 1000] = position.split(':').map(Number)
  const viewport = { top, bottom: top + height }

  // The first row whose bottom is below the top of the drawn band, by binary search.
  const firstAt = (y: number) => {
    let low = 0
    let high = heights.length
    while (low < high) {
      const middle = (low + high) >> 1
      if ((offsets[middle + 1] ?? 0) <= y) low = middle + 1
      else high = middle
    }
    return low
  }
  const start = firstAt(viewport.top - overscanPx)
  let end = start
  while (end < heights.length && (offsets[end] ?? 0) < viewport.bottom + overscanPx) end += 1

  const items = []
  for (let index = start; index < end; index += 1) items.push({ index, top: offsets[index] ?? 0, height: heights[index] ?? 0 })

  /** Scrolls the page so row `index` sits at the top of the screen. */
  const scrollToIndex = useCallback((index: number) => {
    if (!container) return
    window.scrollTo({ top: container.getBoundingClientRect().top + window.scrollY + (offsets[index] ?? 0) })
  }, [container, offsets])

  return { attach: setContainer, total, items, scrollToIndex }
}

function subscribeToViewport(onChange: () => void) {
  window.addEventListener('scroll', onChange, { passive: true })
  window.addEventListener('resize', onChange)
  return () => { window.removeEventListener('scroll', onChange); window.removeEventListener('resize', onChange) }
}
